import {
  AudioLevelMonitor,
  getAudioContext,
  playSpeakerTest,
  type AudioLevel,
} from "./audio";
import { VisionPipeline, type VideoEffectMode } from "./vision";

export const isMobileBrowser = (): boolean =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
  (/Macintosh|MacIntel/i.test(navigator.userAgent) &&
    navigator.maxTouchPoints > 1);

export type CameraLevel = {
  linear: number;
  label: "꺼짐" | "어두움" | "적정" | "밝음";
};

export class LocalMediaController {
  private rawStream: MediaStream | undefined;
  private sendStream: MediaStream | undefined;
  private monitor: AudioLevelMonitor | undefined;
  private audioSource: MediaStreamAudioSourceNode | undefined;
  private audioGain: GainNode | undefined;
  private audioDestination: MediaStreamAudioDestinationNode | undefined;
  private pipeline: VisionPipeline;
  private audioEnabled = true;
  private videoEnabled = true;
  private inputGain = 1;
  private channelMode: "mono" | "stereo" = "mono";
  private outputVolume = 1;
  private outputDeviceId = "";
  private cameraFrame = 0;
  private cameraLastSampleAt = 0;
  private readonly cameraProbe = document.createElement("canvas");

  constructor(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    onVisionState: (state: string) => void,
    private readonly onMicLevel: (level: AudioLevel) => void,
    private readonly onCameraLevel: (level: CameraLevel) => void,
  ) {
    this.pipeline = new VisionPipeline(video, canvas, onVisionState);
    this.video = video;
    this.canvas = canvas;
    this.cameraProbe.width = 32;
    this.cameraProbe.height = 18;
    this.measureCamera = this.measureCamera.bind(this);
  }

  private readonly video: HTMLVideoElement;
  readonly canvas: HTMLCanvasElement;

  async start(): Promise<MediaStream> {
    if (this.rawStream) return this.getSendStream();
    const mobile = isMobileBrowser();
    const videoConstraints = this.videoConstraints("", mobile);
    const audioConstraints = this.audioConstraints();
    try {
      this.rawStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: audioConstraints,
      });
    } catch (error) {
      if (
        error instanceof DOMException &&
        (error.name === "NotAllowedError" || error.name === "SecurityError")
      ) {
        throw error;
      }
      const partialStream = new MediaStream();
      for (const constraints of [
        { audio: audioConstraints, video: false },
        { audio: false, video: videoConstraints },
      ] satisfies MediaStreamConstraints[]) {
        try {
          const stream =
            await navigator.mediaDevices.getUserMedia(constraints);
          for (const track of stream.getTracks()) partialStream.addTrack(track);
        } catch {
          // 장치 하나가 없어도 사용 가능한 다른 장치로 계속 진행합니다.
        }
      }
      if (partialStream.getTracks().length === 0) throw error;
      this.rawStream = partialStream;
    }

    const videoTrack = this.rawStream.getVideoTracks()[0];
    const audioTrack = this.rawStream.getAudioTracks()[0];
    this.audioEnabled = Boolean(audioTrack);
    this.videoEnabled = Boolean(videoTrack);
    if (videoTrack) {
      this.video.srcObject = new MediaStream([videoTrack]);
      await this.video.play();
      await new Promise<void>((resolve) => {
        if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
          resolve();
        } else {
          this.video.addEventListener("loadedmetadata", () => resolve(), {
            once: true,
          });
        }
      });
      this.pipeline.start();
      this.startCameraMonitor();
      this.sendStream = this.canvas.captureStream(mobile ? 20 : 24);
    } else {
      this.sendStream = new MediaStream();
      this.onCameraLevel({ linear: 0, label: "꺼짐" });
    }
    if (audioTrack) {
      this.connectAudioTrack(audioTrack);
      const processedTrack = this.audioDestination?.stream.getAudioTracks()[0];
      if (processedTrack) this.sendStream.addTrack(processedTrack);
    }
    return this.sendStream;
  }

  startWithoutDevices(): MediaStream {
    this.stop();
    this.rawStream = new MediaStream();
    this.sendStream = new MediaStream();
    this.audioEnabled = false;
    this.videoEnabled = false;
    this.onMicLevel({
      linear: 0,
      dbfs: -100,
      clipping: false,
      silent: true,
    });
    this.onCameraLevel({ linear: 0, label: "꺼짐" });
    return this.sendStream;
  }

  getSendStream(): MediaStream {
    if (!this.sendStream) {
      throw new Error("카메라와 마이크가 아직 준비되지 않았습니다.");
    }
    return this.sendStream;
  }

  getRawStream(): MediaStream | undefined {
    return this.rawStream;
  }

  async switchAudioInput(deviceId: string): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: this.audioConstraints(deviceId),
      video: false,
    });
    const nextTrack = stream.getAudioTracks()[0];
    if (!nextTrack) throw new Error("선택한 마이크를 시작하지 못했습니다.");
    const previous = this.rawStream?.getAudioTracks()[0];
    if (!this.rawStream) this.rawStream = new MediaStream();
    if (previous) this.rawStream.removeTrack(previous);
    this.rawStream.addTrack(nextTrack);
    nextTrack.enabled = this.audioEnabled;
    this.connectAudioTrack(nextTrack);
    if (!this.sendStream) this.sendStream = new MediaStream();
    const processedTrack = this.audioDestination?.stream.getAudioTracks()[0];
    if (
      processedTrack &&
      !this.sendStream.getAudioTracks().includes(processedTrack)
    ) {
      this.sendStream.addTrack(processedTrack);
    }
    previous?.stop();
  }

  async switchVideoInput(deviceId: string): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: this.videoConstraints(deviceId, isMobileBrowser()),
    });
    const nextTrack = stream.getVideoTracks()[0];
    if (!nextTrack) throw new Error("선택한 카메라를 시작하지 못했습니다.");
    const previous = this.rawStream?.getVideoTracks()[0];
    if (!this.rawStream) this.rawStream = new MediaStream();
    if (previous) this.rawStream.removeTrack(previous);
    this.rawStream.addTrack(nextTrack);
    nextTrack.enabled = this.videoEnabled;
    this.video.srcObject = new MediaStream([nextTrack]);
    await this.video.play();
    this.startCameraMonitor();
    previous?.stop();
  }

  setInputGain(value: number): void {
    this.inputGain = Math.min(2, Math.max(0, value));
    if (this.audioGain) this.audioGain.gain.value = this.inputGain;
  }

  async setChannelMode(mode: "mono" | "stereo"): Promise<void> {
    this.channelMode = mode;
    const track = this.rawStream?.getAudioTracks()[0];
    if (track) {
      await track.applyConstraints({ channelCount: mode === "mono" ? 1 : 2 });
    }
  }

  setOutputVolume(value: number): void {
    this.outputVolume = Math.min(1, Math.max(0, value));
  }

  setOutputDevice(deviceId: string): void {
    this.outputDeviceId = deviceId;
  }

  getOutputVolume(): number {
    return this.outputVolume;
  }

  getOutputDevice(): string {
    return this.outputDeviceId;
  }

  setEffect(mode: VideoEffectMode): void {
    this.pipeline.setMode(mode);
  }

  getEffect(): VideoEffectMode {
    return this.pipeline.getMode();
  }

  setAvatar(avatar: string): void {
    this.pipeline.setAvatar(avatar);
  }

  setAudioEnabled(enabled: boolean): void {
    this.audioEnabled = enabled && this.hasAudio();
    for (const track of this.rawStream?.getAudioTracks() ?? []) {
      track.enabled = enabled;
    }
  }

  isAudioEnabled(): boolean {
    return this.audioEnabled;
  }

  hasAudio(): boolean {
    return Boolean(this.rawStream?.getAudioTracks().length);
  }

  setVideoEnabled(enabled: boolean): void {
    this.videoEnabled = enabled && this.hasVideo();
    this.pipeline.setCameraEnabled(enabled);
    for (const track of this.rawStream?.getVideoTracks() ?? []) {
      track.enabled = enabled;
    }
    if (!enabled) this.onCameraLevel({ linear: 0, label: "꺼짐" });
  }

  isVideoEnabled(): boolean {
    return this.videoEnabled;
  }

  hasVideo(): boolean {
    return Boolean(this.rawStream?.getVideoTracks().length);
  }

  async testSpeaker(): Promise<void> {
    await playSpeakerTest(this.outputVolume, this.outputDeviceId);
  }

  stop(): void {
    this.monitor?.stop();
    this.monitor = undefined;
    this.audioSource?.disconnect();
    this.audioSource = undefined;
    this.audioGain?.disconnect();
    this.audioGain = undefined;
    this.audioDestination = undefined;
    cancelAnimationFrame(this.cameraFrame);
    this.cameraFrame = 0;
    this.pipeline.stop();
    for (const track of this.rawStream?.getTracks() ?? []) track.stop();
    for (const track of this.sendStream?.getTracks() ?? []) {
      if (!this.rawStream?.getTracks().includes(track)) track.stop();
    }
    this.video.srcObject = null;
    this.rawStream = undefined;
    this.sendStream = undefined;
  }

  private audioConstraints(deviceId = ""): MediaTrackConstraints {
    return {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: this.channelMode === "mono" ? 1 : 2,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    };
  }

  private videoConstraints(
    deviceId: string,
    mobile: boolean,
  ): MediaTrackConstraints {
    return {
      width: mobile ? { ideal: 640, max: 960 } : { ideal: 1280, max: 1280 },
      height: mobile ? { ideal: 360, max: 540 } : { ideal: 720, max: 720 },
      frameRate: mobile ? { ideal: 20, max: 24 } : { ideal: 24, max: 24 },
      ...(deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: "user" }),
    };
  }

  private connectAudioTrack(track: MediaStreamTrack): void {
    const context = getAudioContext();
    this.audioDestination ??= context.createMediaStreamDestination();
    if (!this.audioGain) {
      this.audioGain = context.createGain();
      this.audioGain.connect(this.audioDestination);
    }
    this.audioGain.gain.value = this.inputGain;
    this.audioSource?.disconnect();
    this.audioSource = context.createMediaStreamSource(
      new MediaStream([track]),
    );
    this.audioSource.connect(this.audioGain);
    this.monitor?.stop();
    const processedTrack = this.audioDestination.stream.getAudioTracks()[0];
    if (processedTrack) {
      this.monitor = new AudioLevelMonitor(
        new MediaStream([processedTrack]),
        this.onMicLevel,
      );
    }
  }

  private startCameraMonitor(): void {
    cancelAnimationFrame(this.cameraFrame);
    this.cameraLastSampleAt = 0;
    this.cameraFrame = requestAnimationFrame(this.measureCamera);
  }

  private measureCamera(timestamp: number): void {
    if (!this.rawStream || !this.hasVideo()) return;
    if (
      this.videoEnabled &&
      this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      timestamp - this.cameraLastSampleAt >= 220
    ) {
      this.cameraLastSampleAt = timestamp;
      const context = this.cameraProbe.getContext("2d", {
        willReadFrequently: true,
      });
      if (context) {
        context.drawImage(
          this.video,
          0,
          0,
          this.cameraProbe.width,
          this.cameraProbe.height,
        );
        const pixels = context.getImageData(
          0,
          0,
          this.cameraProbe.width,
          this.cameraProbe.height,
        ).data;
        let luminance = 0;
        for (let index = 0; index < pixels.length; index += 4) {
          luminance +=
            (pixels[index] ?? 0) * 0.2126 +
            (pixels[index + 1] ?? 0) * 0.7152 +
            (pixels[index + 2] ?? 0) * 0.0722;
        }
        const linear = Math.min(
          1,
          Math.max(0, luminance / (pixels.length / 4) / 255),
        );
        const label =
          linear < 0.2 ? "어두움" : linear > 0.82 ? "밝음" : "적정";
        this.onCameraLevel({ linear, label });
      }
    }
    this.cameraFrame = requestAnimationFrame(this.measureCamera);
  }
}
